import React from 'react';
import {
  Button,
  Typography,
  Box,
  FormControl,
  Select,
  MenuItem,
  Alert,
  TextField,
  InputAdornment,
  ListItem,
  ListItemText,
  ListSubheader,
  IconButton,
  CircularProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import GifLogoLoader from '../../../common/loaders/gif-logo-loader';

const StepOne = ({
  currentTheme,
  themeMode,
  areasError,
  loadingAreas,
  selectedArea,
  handleAreaChange,
  areaCodes,
  areaCodeSearch,
  setAreaCodeSearch,
  searchTerm,
  setSearchTerm,
  filteredNumbers,
  loadingNumbers,
  numbersError,
  selectedNumber,
  setSelectedNumber,
  handleContinue,
  handleNumberSearch,
  isSearching,
  searchNumberError,
  searchedNumberResult,
  isVerifyingNumber,
  isNumberVerified,
  verificationError,
}) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', maxWidth: 450 }}>
    <Box sx={{ flex: 1, overflowY: 'auto', paddingRight: '1rem', minHeight: 0 }}>
      <Typography variant="h5" component="h1" fontWeight="bold" sx={{ color: currentTheme.text.primary }}>
        Purchase Fax Number
      </Typography>
      <Typography variant="body2" gutterBottom sx={{ mt: 1, color: currentTheme.text.secondary }}>
        Select or enter your preferred area code to find available fax numbers
      </Typography>

      {areasError && <Alert severity="error" sx={{ width: '100%', mt: 2 }}>{areasError}</Alert>}

      <FormControl fullWidth sx={{ mt: 2 }} size="small">
        <Typography variant="caption" display="block" gutterBottom sx={{ color: currentTheme.text.secondary }}>
          Area Code (USA +1)
        </Typography>
        <Select
          value={selectedArea ? selectedArea.id : ''}
          onChange={handleAreaChange}
          displayEmpty
          disabled={loadingAreas}
          renderValue={(selected) => {
            if (!selected) {
              return <Typography sx={{ color: currentTheme.text.secondary }}>Select or type area code...</Typography>;
            }
            const area = areaCodes.find(a => a.id === selected);
            return area ? `${area.prefix} - ${area.name}, ${area.state}` : '';
          }}
          MenuProps={{
            autoFocus: false,
            PaperProps: {
              sx: {
                maxHeight: 300,
                backgroundColor: themeMode === 'dark' ? currentTheme.homePage.search : "#ffffff",
                border: `1px solid ${themeMode === 'dark' ? currentTheme.border.primary : "#D1D5DB"}`,
                borderRadius: '6px',
                boxShadow: themeMode === 'dark' ? "0 12px 32px rgba(0,0,0,0.6)" : "0 12px 32px rgba(0,0,0,0.15)",
              }
            }
          }}
          sx={{
            color: currentTheme.text.primary,
            backgroundColor: themeMode === 'dark' ? currentTheme.homePage.search : "#ffffff",
            borderRadius: '6px',
            '.MuiOutlinedInput-notchedOutline': {
              borderColor: themeMode === 'dark' ? currentTheme.border.primary : "#D1D5DB",
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: themeMode === 'dark' ? "#4a4867" : "#b0b0b0",
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: currentTheme.common.danger,
            },
            '& .MuiSvgIcon-root': {
              color: currentTheme.text.secondary,
            },
          }}
        >
          <ListSubheader sx={{
            position: 'sticky',
            top: 0,
            zIndex: 1,
            backgroundColor: themeMode === 'dark' ? currentTheme.homePage.search : "#ffffff",
            padding: '8px',
            border: 'none',
            margin: 0,
          }}>
            <TextField
              size="small"
              autoFocus
              placeholder="Type area code or search..."
              fullWidth
              InputProps={{
                style: { color: currentTheme.text.primary },
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: currentTheme.text.secondary }} />
                  </InputAdornment>
                )
              }}
              inputProps={{
                style: { color: currentTheme.text.primary },
              }}
              onChange={(e) => setAreaCodeSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== "Escape") {
                  e.stopPropagation();
                }
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    border: 'none',
                  },
                  backgroundColor: themeMode === 'dark' ? currentTheme.homePage.search : "#ffffff",
                  color: currentTheme.text.primary,
                },
                '& .MuiInputBase-input::placeholder': {
                  color: currentTheme.text.secondary,
                  opacity: 1,
                },
                backgroundColor: themeMode === 'dark' ? currentTheme.homePage.search : "#ffffff",
                borderRadius: '6px',
              }}
            />
            <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 1, mb: 1, color: currentTheme.text.primary }}>Popular Area Codes</Typography>
          </ListSubheader>
          {areaCodes.filter(area => {
            const searchTerm = areaCodeSearch.toLowerCase();
            const areaString = `${area.prefix} - ${area.name}, ${area.state}`.toLowerCase();
            return areaString.includes(searchTerm);
          }).map((area) => (
            <MenuItem key={area.id} value={area.id}
              sx={{
                backgroundColor: themeMode === 'dark' ? currentTheme.homePage.search : "#ffffff",
                color: currentTheme.text.primary,
                borderBottom: `1px solid ${themeMode === 'dark' ? currentTheme.border.primary : "#D1D5DB"}`,
                '&:hover': {
                  backgroundColor: themeMode === 'dark' ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.05)",
                },
                '&.Mui-selected': {
                  backgroundColor: themeMode === 'dark' ? "rgba(42, 43, 74, 0.8)" : "rgba(245, 245, 245, 0.8)",
                },
                '&.Mui-selected:hover': {
                  backgroundColor: themeMode === 'dark' ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)",
                }
              }}
            >
              {`${area.prefix} - ${area.name}, ${area.state}`}
            </MenuItem>
          ))}
        </Select>
        {!selectedArea && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, color: currentTheme.text.secondary }}>
            Select or type a 3-digit area code to see available numbers
          </Typography>
        )}
      </FormControl>

      {selectedArea && (
        <>
          <FormControl fullWidth sx={{ mt: 2 }} size="small">
            <Typography variant="caption" display="block" fontWeight="bold" fontSize={'14px'} mb={1} sx={{ color: currentTheme.text.primary }}>
              Search Numbers
            </Typography>
            <TextField
              value={searchTerm}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9]/g, '');
                if (value.length <= 7) {
                  setSearchTerm(value);
                }
              }}
              placeholder="555-1234"
              size="small"
              InputProps={{
                style: { color: currentTheme.text.primary },
                startAdornment: (
                  <InputAdornment position="start">
                    <Box sx={{
                      backgroundColor: currentTheme.callhistory.searchBackground,
                      color: currentTheme.text.primary,
                      padding: '8px',
                      borderRadius: '4px',
                      marginRight: '8px'
                    }}>
                      ({selectedArea?.prefix})
                    </Box>
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleNumberSearch} disabled={isSearching}>
                      {isSearching ? <CircularProgress size={24} /> : <SearchIcon sx={{ color: currentTheme.text.secondary }} />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '6px',
                  '& fieldset': {
                    borderColor: currentTheme.border.primary,
                  },
                  '&:hover fieldset': {
                    borderColor: themeMode === 'dark' ? "#4a4867" : "#b0b0b0",
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: currentTheme.common.danger,
                  },
                },
              }}
            />
          </FormControl>

          <Typography variant="caption" display="block" sx={{ width: '100%', fontWeight: 'bold', color: currentTheme.text.primary }} fontSize={'14px'}>
            Available Numbers
          </Typography>

          {loadingNumbers && <GifLogoLoader />}
          {searchNumberError && <Alert severity="warning" sx={{ width: '100%', mt: 1 }}>{searchNumberError}</Alert>}
          {numbersError && <Alert severity="warning" sx={{ width: '100%', mt: 1 }}>{numbersError}</Alert>}
          {verificationError && <Alert severity="error" sx={{ width: '100%', mt: 1 }}>{verificationError}</Alert>}

          {!loadingNumbers && !numbersError && (
            <Box sx={{
              width: '100%',
              maxHeight: 280,
              overflow: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
            }}>
              {filteredNumbers.length > 0 ? (
                filteredNumbers.map((number, index) => (
                  <ListItem
                    key={number}
                    button
                    selected={selectedNumber === number}
                    onClick={() => setSelectedNumber(number)}
                    disabled={isVerifyingNumber}
                    sx={{
                      border: selectedNumber === number
                        ? (isNumberVerified ? `2px solid #4CAF50` : `2px solid #FF7052`)
                        : `1px solid ${currentTheme.border.primary}`,
                      borderRadius: '6px',
                      backgroundColor: selectedNumber === number
                        ? (themeMode === 'dark' ? "rgba(255, 112, 82, 0.15)" : "#FFF0ED")
                        : 'transparent',
                      '&:hover': {
                        backgroundColor: selectedNumber === number
                          ? (themeMode === 'dark' ? "rgba(255, 112, 82, 0.15)" : "#FFF0ED")
                          : (themeMode === 'dark' ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.05)")
                      },
                      color: currentTheme.text.primary,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      opacity: isVerifyingNumber && selectedNumber !== number ? 0.5 : 1,
                    }}
                  >
                    <ListItemText primary={`${number.slice(0, 2)} (${selectedArea.prefix}) ${number.slice(5, 8)}-${number.slice(8)}`} />
                    {selectedNumber === number && (
                      isVerifyingNumber ? (
                        <CircularProgress size={20} sx={{ color: '#FF7052' }} />
                      ) : isNumberVerified ? (
                        <CheckCircleIcon sx={{ color: '#4CAF50', fontSize: '20px' }} />
                      ) : (
                        <CheckCircleIcon sx={{ color: '#FF7052', fontSize: '20px' }} />
                      )
                    )}
                  </ListItem>
                ))
              ) : (
                <Box sx={{
                  padding: '16px',
                  textAlign: 'center',
                  color: currentTheme.text.secondary
                }}>
                  {searchedNumberResult && !searchedNumberResult.available ? '' : 'No numbers found.'}
                </Box>
              )}
            </Box>
          )}
        </>
      )}
    </Box>

     <Box display="flex" justifyContent="start" mt={4} pt={2} gap={2} sx={{ flexShrink: 0 }}>
      <Button
        variant="outlined"
        onClick={() => window.history.back()}
        sx={{
          textTransform: 'none',
          borderRadius: '8px',
          borderColor: currentTheme.border.primary,
          color: currentTheme.text.primary,
          '&:hover': {
            backgroundColor: themeMode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
            borderColor: currentTheme.border.primary,
          }
        }}
      >
        Back
      </Button>
      <Button
        variant="contained"
        onClick={handleContinue}
        disabled={!selectedNumber || !isNumberVerified || isVerifyingNumber}
        sx={{
          backgroundColor: currentTheme.common.danger,
          color: '#ffffff',
          '&:hover': {
            backgroundColor: '#E55A3D',
          },
          '&.Mui-disabled': {
            backgroundColor: themeMode === 'dark' ? 'rgba(255, 107, 71, 0.2)' : 'rgba(0, 0, 0, 0.12)',
            color: themeMode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.26)',
          },
          textTransform: 'none',
          borderRadius: '8px',
        }}
      >
        {isVerifyingNumber ? 'Verifying...' : 'Continue'}
      </Button>
    </Box>
  </Box>
);

export default StepOne;
